import { parseUtcMs } from "@/utils/datetime";
import { IconButton, LabelChip, Text, toast } from "@carbon6/c6-frontend-design";
import { useMemo, type ReactNode } from "react";
import type {
  TrainingEvalRunSummaryView,
  TrainingSessionDiagnosticsView,
  TrainingSessionProgressDetailView,
} from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import { CopyButton } from "@/components/buttons/CopyButton";
import { Fact, type LoadState } from "@/components/dataset";
import { NotePanel, type NoteController } from "@/components/note";
import { mlflowRunDeepLink, sessionSimpleStatus } from "@/modules/training/public";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { submittedDatasetLabel } from "../../trainingSessionFormat";
import type { TrainingSessionDetailController } from "../../useTrainingSessionDetailController";
import { SessionBranchHash } from "../SessionBranchHash";
import {
  formatEtaSeconds,
  formatMetricNumber,
  sessionBranchCopyText,
  sessionConfigLabel,
} from "../trainingSessionDetailFormat";
import { checkpointDisplayLabel } from "../trainingSessionDetailFormat";
import { alwaysFetchedMetricKey, type SessionMetricsController } from "../useSessionMetrics";
import {
  buildCheckpointRows,
  resolveSessionBudgetUnit,
  type CheckpointAnalysisRow,
} from "./sessionAnalysisModel";
import { ScenarioEvalSection } from "./ScenarioEvalSection";
import { TrainingCurvesSection } from "./TrainingCurvesSection";
import styles from "./TrainingSessionAnalysisPanel.module.css";

export type TrainingSessionAnalysisPanelProps = {
  controller: TrainingSessionDetailController;
  noteController: NoteController;
  metrics: SessionMetricsController;
  diagnosticsState: LoadState<TrainingSessionDiagnosticsView>;
  // 실패 진단을 다시 받는다. 조회 실패와 미산출 안내의 버튼이 부른다.
  onReloadDiagnostics: () => void;
  summariesState: LoadState<TrainingEvalRunSummaryView[]>;
  selectedMetricKeys: readonly string[];
  onToggleMetricKey: (key: string) => void;
  live: boolean;
};

// 분석 및 평가 탭. 실패 진단(실패 시 최상단) → 학습 요약(논문 인용용) → 학습 곡선 →
// 시나리오별 평가(영상) → 연구 노트 → 디버그 정보 순서로 표시한다.
export function TrainingSessionAnalysisPanel({
  controller,
  noteController,
  metrics,
  diagnosticsState,
  onReloadDiagnostics,
  summariesState,
  selectedMetricKeys,
  onToggleMetricKey,
  live,
}: TrainingSessionAnalysisPanelProps): ReactNode {
  const { session, rich, evalState, progressState, effectiveStatus } = controller;
  const { downloadCheckpoint, downloadingEpoch } = controller;
  const statusKey = sessionSimpleStatus(effectiveStatus).key;
  const isFailure = statusKey === "failed" || statusKey === "action_required";

  const summaries = useMemo(
    () => (summariesState.status === "success" ? summariesState.data : []),
    [summariesState],
  );
  const policies = useMemo(() => rich?.policies ?? [], [rich]);
  const validationHistory = metrics.histories[alwaysFetchedMetricKey];
  const validationPoints = useMemo(
    () => (validationHistory?.status === "success" ? validationHistory.data.points : []),
    [validationHistory],
  );
  const checkpointRows = useMemo(
    () => buildCheckpointRows(policies, validationPoints, summaries),
    [policies, validationPoints, summaries],
  );
  const budgetUnit = useMemo(
    () => resolveSessionBudgetUnit(rich?.recipe, policies, summaries),
    [rich, policies, summaries],
  );
  const checkpointTags = useMemo(
    () => checkpointRows.flatMap((row) => (row.tag !== null ? [row.tag] : [])),
    [checkpointRows],
  );
  const progress = progressState.status === "success" ? progressState.data : null;
  // 아티팩트 다운로드 대상. 최적 체크포인트를 쓰고, 최적 판정이 없으면 마지막 다운로드 가능 체크포인트를 쓴다.
  const downloadEpoch = downloadTargetEpoch(checkpointRows);
  const mlflowUrl = mlflowRunDeepLink(
    rich?.mlflow_run_url,
    rich?.mlflow_experiment_id,
    rich?.mlflow_run_id,
  );

  return (
    <section className={styles.analysis} aria-label="분석 및 Evaluation">
      {isFailure ? (
        <FailureDiagnosisCard
          state={diagnosticsState}
          reload={onReloadDiagnostics}
          canRetry={controller.canRetry}
          retry={controller.retry}
          retryPending={controller.actionPending === "retry"}
          canResubmit={controller.canResubmit}
          resubmit={controller.openResubmit}
        />
      ) : null}

      <section className={styles.card} aria-label="Training 요약">
        <div className={styles.cardHeader}>
          <Text variant={hatcheryTextModes["caption-bold"]}>Training 요약</Text>
          <div className={styles.cardHeaderActions}>
            {mlflowUrl !== null ? (
              <a className={styles.link} href={mlflowUrl} target="_blank" rel="noopener noreferrer">
                MLflow 열기 ↗
              </a>
            ) : rich !== null ? (
              <IconButton
                text="MLflow 열기 ↗"
                size="sm"
                variant="surface"
                onClick={() => toast.info("열 수 있는 MLflow Run 정보가 없습니다.")}
              />
            ) : null}
            <CopyButton
              mode="icon"
              ariaLabel="Training 요약 복사"
              text={buildSummaryClipboardText({
                title: controller.title,
                statusLabel: sessionSimpleStatus(effectiveStatus).label,
                rich,
                metrics,
                budgetUnit,
                progress,
                checkpointRows,
              })}
            />
          </div>
        </div>
        <dl className={styles.summaryFacts}>
          <Fact label="Branch@Hash" value={<SessionBranchHash recipe={rich?.recipe} />} />
          <Fact
            label="Config"
            value={sessionConfigLabel(rich?.recipe, (rich?.mlflow_run_id ?? null) !== null)}
          />
          <Fact label="Training 예산" value={budgetLabel(budgetUnit, progress)} />
          <Fact label="Training 시간" value={durationLabel(rich?.started_at, rich?.finished_at)} />
          <Fact label="최종 training loss" value={lastValueLabel(metrics, "training_loss")} />
          <Fact label="최종 validation loss" value={lastValueLabel(metrics, "validation_loss")} />
          <Fact label="최적 Checkpoint" value={bestCheckpointLabel(checkpointRows)} />
        </dl>
        {downloadEpoch !== null ? (
          <dl className={styles.wideFacts}>
            {/* 브라우저는 gs:// URI 를 받을 수 없으므로 signed URL 다운로드 링크만 둔다. */}
            <Fact
              label="Artifact"
              value={
                <button
                  type="button"
                  className={styles.linkButton}
                  disabled={downloadingEpoch !== null}
                  onClick={() => void downloadCheckpoint(downloadEpoch)}
                >
                  {downloadingEpoch !== null
                    ? "준비 중…"
                    : `${checkpointDisplayLabel(downloadEpoch)} Checkpoint 다운로드`}
                </button>
              }
              wide
            />
          </dl>
        ) : null}
      </section>

      <TrainingCurvesSection
        metrics={metrics}
        selectedKeys={selectedMetricKeys}
        onToggleKey={onToggleMetricKey}
        budgetUnit={budgetUnit}
        overrides={rich?.recipe?.config.overrides ?? null}
        checkpointTags={checkpointTags}
        live={live}
      />

      <ScenarioEvalSection
        evalState={evalState}
        summaries={summaries}
        onRetry={controller.refresh}
      />

      <section className={styles.card} aria-label="연구 노트">
        <div className={styles.cardHeader}>
          <Text variant={hatcheryTextModes["caption-bold"]}>연구 노트</Text>
        </div>
        <NotePanel controller={noteController} />
      </section>

      <section className={styles.card} aria-label="디버그 정보">
        <div className={styles.cardHeader}>
          <Text variant={hatcheryTextModes["caption-bold"]}>디버그 정보</Text>
        </div>
        <dl className={styles.debugFacts}>
          <Fact label="Experiment" value={session.experiment_id ?? rich?.experiment_id ?? "—"} />
          <Fact label="Session" value={session.id} />
          <Fact label="MLflow run" value={rich?.mlflow_run_id ?? "—"} />
          <Fact label="MLflow experiment" value={rich?.mlflow_experiment_id ?? "—"} />
        </dl>
      </section>
    </section>
  );
}

// ── 실패 진단 ────────────────────────────────────────────────────────────────

// 분류 어휘 → 표시 라벨·색. 모르는 분류는 원문 그대로 중립색으로 표시한다.
function classificationChip(classification: string): { label: string; color: number } {
  if (classification === "transient") {
    return { label: "일시 오류", color: 0xd97706 };
  }
  if (classification === "deterministic") {
    return { label: "설정·코드 원인", color: 0xdc2626 };
  }
  return { label: classification, color: 0x64748b };
}

function FailureDiagnosisCard({
  state,
  reload,
  canRetry,
  retry,
  retryPending,
  canResubmit,
  resubmit,
}: {
  state: LoadState<TrainingSessionDiagnosticsView>;
  // 진단만 다시 받는다. `retry` 는 학습을 다시 실행한다.
  reload: () => void;
  canRetry: boolean;
  retry: () => void;
  retryPending: boolean;
  canResubmit: boolean;
  resubmit: () => void;
}): ReactNode {
  return (
    <section className={styles.diagnosisCard} aria-label="실패 진단">
      <div className={styles.cardHeader}>
        <Text variant={hatcheryTextModes["caption-bold"]}>실패 진단</Text>
        {/* 2가지 복구: 수정 없는 재실행은 재시도, 설정을 고쳐야 하는 실패(deterministic)는 프리필된
            신규 제출이다. 진단을 읽은 사용자가 버튼을 골라 판단한다. */}
        {canResubmit ? (
          <IconButton
            text="설정 고쳐 다시 제출"
            size="sm"
            variant="surface"
            disabled={retryPending}
            onClick={resubmit}
          />
        ) : null}
        {canRetry ? (
          <IconButton
            text={retryPending ? "재시도 중…" : "재시도"}
            size="sm"
            variant="surface"
            disabled={retryPending}
            onClick={retry}
          />
        ) : null}
      </div>
      {state.status === "idle" || state.status === "loading" ? (
        <LoadingState size="sm">진단 정보를 불러오고 있습니다.</LoadingState>
      ) : state.status === "error" ? (
        <EmptyState size="sm" tone="error" action={{ label: "다시 시도", onClick: reload }}>
          진단 정보를 불러오지 못했습니다: {state.message}
        </EmptyState>
      ) : (
        <DiagnosisBody diagnostics={state.data} reload={reload} />
      )}
    </section>
  );
}

function DiagnosisBody({
  diagnostics,
  reload,
}: {
  diagnostics: TrainingSessionDiagnosticsView;
  reload: () => void;
}): ReactNode {
  if (diagnostics.message === null && diagnostics.classification === null) {
    return (
      <EmptyState size="sm" action={{ label: "새로고침", onClick: reload }}>
        실패 원인 진단이 아직 산출되지 않았습니다. 잠시 후 새로고침해 주세요.
      </EmptyState>
    );
  }
  const rawErrors = [diagnostics.step_error_message, diagnostics.attempt_error_message].filter(
    (message): message is string => message !== null && message.trim() !== "",
  );
  return (
    <div className={styles.diagnosisBody}>
      <div className={styles.diagnosisChips}>
        {diagnostics.classification !== null ? (
          <LabelChip
            color={classificationChip(diagnostics.classification).color}
            size="xs"
            variant="filled"
          >
            {classificationChip(diagnostics.classification).label}
          </LabelChip>
        ) : null}
        {diagnostics.reason !== null ? (
          <span className={styles.reasonCode}>{diagnostics.reason}</span>
        ) : null}
      </div>
      {diagnostics.message !== null ? (
        <p className={styles.diagnosisMessage}>{diagnostics.message}</p>
      ) : null}
      {diagnostics.user_action !== null && diagnostics.user_action.trim() !== "" ? (
        <Fact label="권장 조치" value={diagnostics.user_action} wide />
      ) : null}
      {rawErrors.length > 0 ? (
        <details className={styles.rawErrors}>
          <summary>원본 오류 메시지</summary>
          {rawErrors.map((message) => (
            <pre key={message} className={styles.rawErrorText}>
              {message}
            </pre>
          ))}
        </details>
      ) : null}
    </div>
  );
}

// ── 학습 요약 파생 ───────────────────────────────────────────────────────────

function budgetLabel(
  budgetUnit: "epoch" | "step",
  progress: TrainingSessionProgressDetailView | null,
): string {
  if (budgetUnit === "step") {
    const target = progress?.target_step ?? null;
    return target !== null ? `STEP ${formatMetricNumber(target)}` : "STEP 기준";
  }
  const total = progress?.total_epochs ?? null;
  return total !== null ? `EPOCH ${formatMetricNumber(total)}` : "EPOCH 기준";
}

function durationLabel(
  startedAt: string | null | undefined,
  finishedAt: string | null | undefined,
): string {
  // 두 값을 같은 방식으로 파싱해 빼므로 잘못 파싱해도 offset 이 상쇄되어 길이는 맞다. 그래도 공용
  // 파서를 쓰는 이유가 있다. 한쪽만 zone 을 가지고 오면(상류가 필드마다 다르게 직렬화하면) 차이
  // 전체가 틀린다.
  const startMs = parseUtcMs(startedAt ?? null);
  const finishMs = parseUtcMs(finishedAt ?? null);
  if (startMs === null || finishMs === null) {
    return "—";
  }
  const seconds = (finishMs - startMs) / 1000;
  if (!Number.isFinite(seconds) || seconds < 0) {
    return "—";
  }
  return formatEtaSeconds(seconds);
}

function lastValueLabel(metrics: SessionMetricsController, metricKey: string): string {
  if (metrics.metricsState.status !== "success") {
    return "—";
  }
  const value = metrics.metricsState.data.last_values[metricKey];
  return value !== undefined ? formatMetricNumber(value) : "—";
}

// 최적 체크포인트의 태그를 쓰고, 없으면 마지막 다운로드 가능 체크포인트의 태그를 쓴다. 태그 없는
// 산출물(카탈로그 등록 정책의 artifact 가 태그 디렉터리 밖)은 다운로드 경로가 없어 대상에서 뺀다.
// 받을 것이 없으면 null 이다.
function downloadTargetEpoch(rows: readonly CheckpointAnalysisRow[]): string | null {
  const best = rows.find((row) => row.isBest);
  const target = best ?? [...rows].reverse().find((row) => row.checkpoint.epoch !== null);
  return target?.checkpoint.epoch ?? null;
}

function bestCheckpointLabel(rows: readonly CheckpointAnalysisRow[]): string {
  const best = rows.find((row) => row.isBest);
  if (best === undefined || best.loss.value === null) {
    return "—";
  }
  return `${checkpointDisplayLabel(best.checkpoint.epoch)} · val loss ${formatMetricNumber(best.loss.value)}`;
}

// 논문·보고서에 붙여 넣는 요약 텍스트 한 단위. 화면의 요약 값과 같은 소스에서 만든다.
function buildSummaryClipboardText(input: {
  title: string;
  statusLabel: string;
  rich: TrainingSessionDetailController["rich"];
  metrics: SessionMetricsController;
  budgetUnit: "epoch" | "step";
  progress: TrainingSessionProgressDetailView | null;
  checkpointRows: readonly CheckpointAnalysisRow[];
}): string {
  const { title, statusLabel, rich, metrics, budgetUnit, progress, checkpointRows } = input;
  // 화면과 같은 규칙이다. training 카탈로그 이름은 포함하지 않는다. registry 원본이 없는 입력은
  // training-infra 전용 표기로 포함한다.
  const datasets = (rich?.datasets ?? []).map(submittedDatasetLabel).join(", ");
  const lines = [
    `Session: ${title}`,
    `상태: ${statusLabel}`,
    `Dataset: ${datasets || "—"}`,
    `Branch@Hash: ${sessionBranchCopyText(rich?.recipe)}`,
    `Training 예산: ${budgetLabel(budgetUnit, progress)}`,
    `Training 시간: ${durationLabel(rich?.started_at, rich?.finished_at)}`,
    `최종 training loss: ${lastValueLabel(metrics, "training_loss")}`,
    `최종 validation loss: ${lastValueLabel(metrics, "validation_loss")}`,
    `최적 Checkpoint: ${bestCheckpointLabel(checkpointRows)}`,
  ];
  return lines.join("\n");
}
